import { Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { last } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CHART_GREEN } from '../../theme/theme-colors';
import type { Graph, PythonStreamData, Series } from '../Dashboard/dashboardTypes';
import { GraphNoDataMessage } from './GraphNoDataMessage';
import { chartSeriesLabel } from './chartSeriesLabel';

export const POSITION_X_KEY = 'Position X';
export const POSITION_Y_KEY = 'Position Y';

/**
 * Live stream rows are often partial (one pythonStream packet may only include a subset of keys).
 * The position chart needs (X, Y) pairs; carry forward the last known X/Y and add a point when
 * either coordinate updates in a row and both are known.
 */
export function buildPositionPathDataset(data: ReadonlyArray<Record<string, unknown>>): PythonStreamData[] {
  let lastX: number | undefined;
  let lastY: number | undefined;
  const out: PythonStreamData[] = [];
  let fallbackIndex = 0;

  for (const datum of data) {
    const x = datum[POSITION_X_KEY];
    const y = datum[POSITION_Y_KEY];
    const xNum = typeof x === 'number' && Number.isFinite(x) ? x : undefined;
    const yNum = typeof y === 'number' && Number.isFinite(y) ? y : undefined;
    const hasX = Object.prototype.hasOwnProperty.call(datum, POSITION_X_KEY);
    const hasY = Object.prototype.hasOwnProperty.call(datum, POSITION_Y_KEY);
    const hasInvalidCoordinate = (hasX && xNum === undefined) || (hasY && yNum === undefined);

    if (hasInvalidCoordinate) {
      lastX = undefined;
      lastY = undefined;
      continue;
    }

    if (xNum !== undefined) lastX = xNum;
    if (yNum !== undefined) lastY = yNum;

    if (lastX !== undefined && lastY !== undefined && (xNum !== undefined || yNum !== undefined)) {
      const t0 = datum.time;
      const time = typeof t0 === 'number' && Number.isFinite(t0) ? t0 : fallbackIndex;
      out.push({
        time,
        [POSITION_X_KEY]: lastX,
        [POSITION_Y_KEY]: lastY,
      });
      fallbackIndex += 1;
    }
  }

  return out;
}

const defaultPositionSeries: Series[] = [{ showMark: false, curve: 'linear', dataKey: POSITION_Y_KEY, color: CHART_GREEN }];

const chartSX = {
  [`& .${axisClasses.left} .${axisClasses.label}`]: {
    transform: 'translateX(-10px)',
  },
};

type PositionGraphSize = {
  size: number;
  box: object;
  title?: object;
};

interface PositionGraphProps {
  graph: Graph;
  size: PositionGraphSize;
  disableAxisListener?: boolean;
  live?: boolean;
}

const PositionGraph = ({ graph, size, disableAxisListener, live }: PositionGraphProps) => {
  const [maxRange, setMaxRange] = useState(200);
  const { t } = useTranslation();

  const positionX = useMemo(
    () => ({ label: t('chart.xAxis.pos', 'X (cm)'), dataKey: POSITION_X_KEY, scaleType: 'linear' as const, min: -200, max: 200 }),
    [t]
  );
  const positionY = useMemo(
    () => ({ label: t('chart.yAxis.pos', 'Y (cm)'), dataKey: POSITION_Y_KEY, scaleType: 'linear' as const, min: -200, max: 200 }),
    [t]
  );

  const positionDataset = useMemo(() => buildPositionPathDataset(graph.data), [graph.data]);

  const effectiveSeries = useMemo(() => {
    const base = graph.series?.length ? graph.series : defaultPositionSeries;
    const ySeries = base.find((s) => s.dataKey === POSITION_Y_KEY) ?? defaultPositionSeries[0];
    return [{ ...ySeries, dataKey: POSITION_Y_KEY, showMark: false, label: chartSeriesLabel(t, POSITION_Y_KEY) }];
  }, [graph.series, t]);

  const range = useMemo(() => {
    const allValues: number[] = [];

    if (live) {
      const lastDatum = last(positionDataset);
      if (lastDatum) {
        const x = lastDatum[POSITION_X_KEY];
        const y = lastDatum[POSITION_Y_KEY];
        if (typeof x === 'number') allValues.push(Math.abs(x));
        if (typeof y === 'number') allValues.push(Math.abs(y));
      }
    } else {
      positionDataset.forEach((datum) => {
        const x = datum[POSITION_X_KEY];
        const y = datum[POSITION_Y_KEY];
        if (typeof x === 'number') allValues.push(Math.abs(x));
        if (typeof y === 'number') allValues.push(Math.abs(y));
      });
    }

    return allValues.length ? Math.max(maxRange, ...allValues) : maxRange;
  }, [positionDataset, live, maxRange]);

  useEffect(() => {
    setMaxRange(range);
  }, [range]);

  const graphValues = useMemo(() => {
    const translatedGraph: Graph = { ...graph };
    translatedGraph.xAxis = [{ ...positionX, max: +range, min: -range }];
    translatedGraph.yAxis = [{ ...positionY, max: +range, min: -range }];
    return translatedGraph;
  }, [graph, positionX, positionY, range]);

  return (
    <Box sx={size.box}>
      {positionDataset.length > 0 ? (
        <LineChart
          hideLegend
          skipAnimation
          dataset={positionDataset}
          xAxis={graphValues.xAxis}
          yAxis={graphValues.yAxis || undefined}
          series={effectiveSeries}
          height={size.size}
          width={size.size}
          sx={chartSX}
          disableAxisListener={disableAxisListener}
          grid={{ vertical: true, horizontal: true }}
        />
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight={size.size}>
          <GraphNoDataMessage />
        </Box>
      )}
    </Box>
  );
};

export default PositionGraph;
